import type {MedicalTransporterType, UserPermissionType} from '@ambler/prisma'
import {toastError} from '@ambler/andive-next'
import {gql, useMutation, useQuery} from '@apollo/client'
import type {ApolloError} from '@apollo/client'
import {clearConnectAsSessionId} from '@ambler/shared-front'
import React from 'react'
import {apiFetch, apiOrigin} from '../../lib/api'
import {getUpdateDevicePayload} from '../../lib/firebase-messaging'
import {
  AuthSessionContext,
  SIGNED_OUT_PATH,
  STAFF_IDP_HINT,
  onSessionRefused,
  refusesSession,
  signInReturnUrl,
  useAuthSession,
} from '../../lib/auth/session'
import type {AuthSession} from '../../lib/auth/session'
import {ErrorCard} from '../error'
import type {BO_logoutDeviceMutation_, BO_logoutDeviceMutationVariables, BO_meQuery_} from './auth.generated'
import {useProfile} from './profile'

type MtAcl = {
  canRead: boolean
  canWrite: boolean
  mt: MedicalTransporter
}

const logoutDeviceMutation = gql`
  mutation BO_logoutDeviceMutation($data: PushNotificationsUpsertDeviceDataInput!) {
    pushNotificationsUpsertDevice(data: $data)
  }
`

// The identity comes from the API, which resolves it per request, so a permission
// change takes effect on the next one instead of waiting for a new token.
export const meQuery = gql`
  query BO_meQuery {
    me {
      id
      auth0Id
      name
      email
      role
      isEmailConfirmed
      isConnectAs
      permissions {
        id
        type
      }
    }
  }
`

const isApiConfigured = () => {
  try {
    apiOrigin()
    return true
  } catch (_e) {
    return false
  }
}

/**
 * The page cannot read the HttpOnly session cookie: `me` is its only witness.
 * Mounted once, above every consumer: remembering whether the tab ever had an
 * identity is what tells a lost session from an absent one.
 */
export const AuthSessionProvider: React.FC = ({children}) => {
  // Whatever the client's defaults: a remount of this provider must find the
  // identity the tab had, or a lost session would pass for an absent one.
  const {data, loading, error} = useQuery<BO_meQuery_>(meQuery, {fetchPolicy: 'cache-first'})
  const [logoutDevice] = useMutation<BO_logoutDeviceMutation_, BO_logoutDeviceMutationVariables>(logoutDeviceMutation)

  const hadIdentityRef = React.useRef(false)
  const [sessionLost, setSessionLost] = React.useState(false)

  React.useEffect(() => {
    if (data?.me) {
      hadIdentityRef.current = true
    }
  }, [data])

  // Without an identity loaded, a refusal is a first visit: _app signs in.
  React.useEffect(
    () =>
      onSessionRefused(() => {
        if (hadIdentityRef.current) {
          setSessionLost(true)
        }
      }),
    [],
  )

  // One redirect at a time: the sign-in effect in _app re-fires on every state
  // change and would otherwise stack navigations.
  const redirectStartedRef = React.useRef(false)

  // A page restored from the back/forward cache (Back from the login page) comes
  // back as it left, mid-redirect: nothing would start the sign-in again, and the
  // tab would sit on its loader. A reload gives it a fresh start.
  // https://developer.mozilla.org/en-US/docs/Glossary/bfcache
  React.useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted && redirectStartedRef.current) {
        window.location.reload()
      }
    }

    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])

  // One sign-out at a time: a caller re-rendering on the way (the non-staff
  // check of _app) would otherwise send it again.
  const signOutRef = React.useRef<Promise<boolean> | undefined>(undefined)

  const sessionRefused = refusesSession(error?.graphQLErrors)

  const value = React.useMemo<AuthSession>(
    () => ({
      me: data?.me,
      isAuthenticated: Boolean(data?.me) && !sessionLost,
      isLoading: loading,
      isSessionLost: sessionLost,
      identityError: error && !sessionRefused ? error : undefined,
      signIn: ({returnTo} = {}) => {
        if (redirectStartedRef.current) {
          return
        }
        redirectStartedRef.current = true

        const login = new URL('/auth/login', apiOrigin())
        login.searchParams.set('returnTo', signInReturnUrl(returnTo, window.location.origin).toString())
        login.searchParams.set('idp', STAFF_IDP_HINT)

        window.location.assign(login.toString())
      },
      signOut: () => {
        if (!signOutRef.current) {
          signOutRef.current = (async () => {
            try {
              await logoutDevice({
                variables: {
                  data: {
                    ...(await getUpdateDevicePayload()),
                    state: 'LOGGED_OUT',
                    fcmToken: null,
                  },
                },
              })
            } catch (_err) {
              // Best effort: the session has to end whatever the device says.
            }

            try {
              const logout = new URL('/auth/logout', apiOrigin())
              logout.searchParams.set('returnTo', `${window.location.origin}${SIGNED_OUT_PATH}`)

              const response = await apiFetch(logout.toString(), {method: 'POST'})
              if (!response.ok) {
                throw new Error(`Logout refused: ${response.status}`)
              }

              // The tab's impersonation id survives the trip to Keycloak: signing
              // back in here would silently resume impersonating.
              const {endSessionUrl} = (await response.json()) as {endSessionUrl: string}
              clearConnectAsSessionId()
              window.location.assign(endSessionUrl)
              return true
            } catch (_err) {
              // The session is still alive: the sign-out page would send the user
              // straight back in. Say it, and stay.
              toastError('La déconnexion a échoué. Veuillez réessayer.')
              signOutRef.current = undefined
              return false
            }
          })()
        }

        return signOutRef.current
      },
    }),
    [data, loading, error, sessionRefused, sessionLost, logoutDevice],
  )

  if (!isApiConfigured()) {
    return (
      <ErrorCard error={{message: "L'adresse de l'API n'est pas configurée sur ce déploiement (GRAPHQL_ENDPOINT)."}} />
    )
  }

  return <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>
}

export const useAuth = (): {
  user: {
    id: string
    auth0Id: string
    name: string
    email: string
    role: string
  }
  mtAcls: MtAcl[]
  isAuthenticated: boolean
  isEmailConfirmed: boolean
  isLoading: boolean
  isConnectAs: boolean
  // The identity is fetched, so it can fail: consumers must be able to tell
  // "failed" from "loaded and empty", which look alike otherwise.
  identityError: ApolloError | undefined
  isSessionLost: AuthSession['isSessionLost']
  signIn: AuthSession['signIn']
  signOut: AuthSession['signOut']
  permissions: {id: string; type: UserPermissionType}[]
} => {
  const {me, isAuthenticated, isLoading, isSessionLost, identityError, signIn, signOut} = useAuthSession()

  const {id, auth0Id, name, email, role, isEmailConfirmed, isConnectAs, permissions} = me ?? {}

  const {mts, isLoading: isProfileLoading} = useProfile()

  return {
    user: {
      id,
      auth0Id,
      name,
      email,
      role,
    },
    mtAcls: mts.map(mtAcl => ({
      mt: mtAcl.mt,
      canRead: mtAcl.acl.canRead,
      canWrite: mtAcl.acl.canWrite,
    })),
    isAuthenticated,
    isEmailConfirmed,
    permissions,
    // Anything gating on the identity has to wait for this: an absent identity is
    // indistinguishable from a non-staff one.
    isLoading: isLoading || isProfileLoading,
    isSessionLost,
    isConnectAs,
    identityError,
    signIn,
    signOut,
  }
}

type MedicalTransporter = {
  id: string
  name?: string
  type: MedicalTransporterType
}

type MainMt = {
  canRead: boolean
  canWrite: boolean
  mt: MedicalTransporter
}

export const useMainMt = (): MainMt => {
  const {isLoading, isAuthenticated, mtAcls} = useAuth()

  if (isLoading || !isAuthenticated) {
    return null
  }

  return mtAcls?.[0]
}
